import React, {useEffect, useMemo, useRef, useState} from 'react';
import { getLocale } from 'umi';
import { getIfPercentage, isTooltipShowAll } from '@intelligence/utils/common';
import {
  getCountryNameByCountryCode,
  getSortedDates,
  showTimeByDateType,
} from '@/pages/intelligence/utils/commonUtils';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { ChartType } from '@/pages/intelligence/pages/TopCharts/_config/enums';
import OpinionBarAndLineChart from '@intelligence/v3/components/OpinionBarAndLineChart';
import useLineCharts from '@/pages/intelligence/components/Chart/hooks/useLineCharts';
import useSinglePieCharts from '@/pages/intelligence/components/Chart/hooks/useSinglePieCharts';
import type { IChartsIndexInstance } from '@intelligence/v3/components/OpinionBarAndLineChart';
import type { IOpinionEventConfig } from '@intelligence/v3/components/OpinionBarAndLineChart/hooks/types';
import type { IAlineaTrueDataSeries } from '../../utils/alineaTrueData';
import useMarketFilterApi, { MarketSpecailType } from '@/pages/intelligence/hooks/useMarketSelector';
import { allMetricsSourcesOption } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import {
  isKpiTrendsHourlyGranularity,
  applyKpiTrendsHourlyLineXAxis,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/hourSegmentUtils';
import {
  assignDistinctSeriesColors,
  buildVisualMetricAxisInfo,
  expandVisualMetricSeriesItems,
  getDualAxisHeaderTexts,
  getMetricDisplayText,
  isMultiValuePriceMetric,
} from '@intelligence/pages/TopCharts/utils/multiValueMetricUtils';
import {
  applySeriesLegendDisplay,
  formatTrueDataSeriesName,
  stripLeadingMetricQualifiers,
  TRUE_DATA_CHART_DIAMOND_SIZE,
} from '@intelligence/pages/TopCharts/utils/seriesDisplay';
import { ECHARTSCOLOR } from '@intelligence/utils/echartsConst';
import './index.scss';

const ALINEA_TRUE_DATA_COLORS = [ECHARTSCOLOR[3], ECHARTSCOLOR[4]];

// 每个榜单，组装好各类可视化需要的数据，传递给可视化统一组件
const Visualis: React.FC<any> = (props: any) => {
  const {
    chartType = ChartType.Line,
    dateType,
    startDate,
    endDate,
    lineData: lineDataTemp = [], // 折线 数据
    isNodata,
    imageType,
    titleNow,
    chartLoading,
    setChartLoading,
    topChartsType,
    marketLineLoading,
    ifUseMarketLineLoading = false,
    tableData,
    barData: barDataTemp,
    calOtherList,
    gameName,
    regionsCountries,
    baseReleaseTime,
    earliestPeriod,
    // regionSegVal,
    lineSeriesFormatter,
    isStepLine,
    lineMetricName,
    visualisLineTopChartsType,
    // visualisBarTopChartsType,
    visualisPieTopChartsType,
    isNaNeedMax,
    byDevice = false,
    byCountry = false,
    isStackLine,
    // barSeriesFormatter,
    pieSeriesFormatter,
    // barNameListFormatter,
    pieData: pieDataTemp,
    isCountriesPage = false,
    visualMetricValue,
    visualMetricValues: visualMetricValuesIds,
    alineaTrueDataSeries = [],
    tableMetricList = [],
    chartHeight,
    chartNodataMsg,
    legendSelected,
    secondTitleRight,
    lineSourceName,
    ifLineChartLegendOn = false,
    lineSourceIcon,
    gridConfig,
    enableOpinionEventAnnotation,
    controlledEventAnnotation,
    opinionEventConfig,
  } = props;

  const { formatMessage } = useIntlExtend();
  const alineaTrueDataSuffix = formatMessage({
    id: 'dbi_alinea_true_data_suffix',
  });
  const locale = getLocale(); // 中英切换方法
  const cardRef = useRef(null);
  const chartsInstance = useRef<IChartsIndexInstance>(null);
  const [earliestClickValue, setEarliestClickValue] = useState<any>('');

  // 自行获取国家列表，用于按国家饼图的国家名转换（外部传入的 regionsCountries 时机不稳定）
  const { regionOptions: localRegionOptions } = useMarketFilterApi({
    filterType: MarketSpecailType.metricTable,
  });
  // 优先使用内部加载的 regionOptions，回退到外部传入的 regionsCountries
  const countryOptions = (localRegionOptions?.length ? localRegionOptions : regionsCountries) || [];

  const isPercentage = useMemo(
    () => getIfPercentage(visualMetricValue),
    [visualMetricValue],
  );
  const isCurrentMetricTooltipShowAll = useMemo(
    () => isTooltipShowAll(visualMetricValue),
    [visualMetricValue],
  );

  const visualMetricValueInfo = useMemo(() => {
    const metricObj = tableMetricList?.find(
      (item: any) => item.value === visualMetricValue,
    );

    return {
      value: metricObj?.value,
      text: formatMessage({ id: metricObj?.text }),
      renderType: metricObj?.renderType,
    };
  }, [topChartsType, visualMetricValue, tableMetricList, locale]);

  // 同名指标在不同数据源下使用相同的 i18n 文案（如 Alinea/Gamalytic 的 "Steam本体收入(美元)"），
  // 直接作为图例 name 会因 echarts 同名合并而只展示一条线。这里仅针对会出现同名冲突的
  // alinea / gamalytic / vginsights 三个数据源给图例文案加上数据源前缀，其他数据源不加前缀。
  // 注意：该前缀仅用于解决同名合并，不影响数据源 icon 的展示——icon 由
  // getSeriesSourceDisplayMeta 统一附加，覆盖所有数据源曲线。
  const PREFIX_DATASOURCES = ['alinea', 'gamalytic', 'vginsights'];

  const getMetricLegendText = useMemo(
    () => (metricObj: any): string => {
      const baseText = getMetricDisplayText(metricObj, formatMessage);
      const dataSource = metricObj?.dataSource;
      if (!dataSource || !PREFIX_DATASOURCES.includes(dataSource)) return baseText;
      const sourceItem = allMetricsSourcesOption.find(
        (s: any) => s?.value === dataSource,
      );
      if (!sourceItem || !sourceItem.text) return baseText;
      // text 字段可能是 i18n key（如 dbi_other_vendor）也可能是字面量（如 'Alinea Analytics'）
      const sourceLabel = formatMessage({
        id: sourceItem.text,
        defaultMessage: sourceItem.text,
      });
      if (!sourceLabel) return baseText;
      // 已带数据源前缀的文案不重复添加（防御 i18n 异常或后续业务改动）
      if (baseText && baseText.indexOf(sourceLabel) === 0) return baseText;
      return `${sourceLabel} ${baseText}`;
    },
    [formatMessage, locale],
  );

  const getSeriesSourceDisplayMeta = (metricObj: any) => {
    const sourceItem = allMetricsSourcesOption.find(
      (item: any) => item?.value === metricObj?.dataSource,
    );
    const sourceLabel = sourceItem?.text
      ? formatMessage({
        id: sourceItem.text,
        defaultMessage: sourceItem.text,
      })
      : '';
    return {
      dataSourceKey: sourceItem?.icon || metricObj?.dataSource || '',
      // 数据源前缀已用于解决同名 series，图例和 Tooltip 改由 icon 表达后只展示指标正文。
      dbTooltipName: stripLeadingMetricQualifiers(metricObj?.text || '', [
        sourceLabel,
        metricObj?.dataSource || '',
      ]),
    };
  };

  const visualMetricAxisInfo = useMemo(
    () =>
      buildVisualMetricAxisInfo(visualMetricValuesIds, {
        tableMetricList,
        formatMessage,
        formatLegendText: getMetricLegendText,
      }),
    [
      JSON.stringify(visualMetricValuesIds),
      tableMetricList,
      locale,
      getMetricLegendText,
      formatMessage,
    ],
  );

  const visualMetricValuesInfo = useMemo(
    () =>
      expandVisualMetricSeriesItems(
        visualMetricAxisInfo,
        lineDataTemp,
        formatMessage,
      ),
    [visualMetricAxisInfo, lineDataTemp, formatMessage],
  );

  const axisHeaderTexts = useMemo(
    () => getDualAxisHeaderTexts(visualMetricAxisInfo),
    [visualMetricAxisInfo],
  );

  const showExternalAxisTitles = axisHeaderTexts.length === 2;

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const lineData = useMemo(() => {
    if (
      lineDataTemp &&
      Array.isArray(lineDataTemp) &&
      lineDataTemp?.length > 0
    ) {
      return lineDataTemp?.map((item: any) => {
        if (item.date) {
          return {
            ...item,
            date: showTimeByDateType(item.date, dateType?.toLocaleLowerCase()),
          };
        }
        return item;
      });
    }
    return lineDataTemp;
  }, [JSON.stringify(lineDataTemp), dateType]);

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const barData = useMemo(() => {
    if (barDataTemp && Array.isArray(barDataTemp) && barDataTemp?.length > 5) {
      return barDataTemp?.slice(0, 5);
    }
    return barDataTemp;
  }, [JSON.stringify(barDataTemp)]);

  // 拿到数据，先按规则要求处理，再传给可视化使用
  const pieData = useMemo(() => {
    return pieDataTemp;
  }, [JSON.stringify(pieDataTemp)]);

  const isHoverPie = useMemo(() => {
    if (isCountriesPage) return false;
    return imageType === 'combine';
  }, [topChartsType, imageType, chartType]);

  // 第一步：按国家汇总 pieData，得到 [{market, value}] 列表（不依赖 regionsCountries）
  const countryPieRaw = useMemo(() => {
    if (!byCountry || !Array.isArray(pieData) || pieData.length === 0) return null;
    const metricSuffix = visualMetricValue?.includes('__')
      ? visualMetricValue.split('__').slice(1).join('__')
      : visualMetricValue;
    const marketTotalMap: Record<string, number> = {};
    pieData.forEach((item: any) => {
      const metric = item?.metric || item?.name;
      if (metric !== visualMetricValue && metric !== metricSuffix) return;
      const market = item?.market || '';
      if (!market) return;
      marketTotalMap[market] = (marketTotalMap[market] || 0) + (Number(item?.value) || 0);
    });
    if (Object.keys(marketTotalMap).length === 0) return null;
    const sorted = Object.entries(marketTotalMap).sort(([, a], [, b]) => b - a);
    const TOP = 10;
    const top = sorted.slice(0, TOP);
    const rest = sorted.slice(TOP);
    const othersVal = rest.reduce((sum: number, [, v]: [string, number]) => sum + v, 0);
    return { top, othersVal };
  }, [JSON.stringify(pieData), visualMetricValue, byCountry]);

  // 第二步：将 market code 映射为国家名（依赖 regionsCountries，确保加载后重新映射）
  const pieListData = useMemo(() => {
    if (isHoverPie) {
      if (byCountry && countryPieRaw) {
        const { top, othersVal } = countryPieRaw;
        const result = (top as [string, number][]).map(([market, value]) => {
          const countryName = getCountryNameByCountryCode(countryOptions, market);
          return { name: countryName, name_zh: countryName, app_id: market, value };
        });
        if (othersVal > 0) {
          result.push({ name: 'Others', name_zh: 'Others', app_id: 'others', value: othersVal });
        }
        return result;
      }
      return (
        pieSeriesFormatter?.({
          pieData,
          legendMetric: visualMetricValue,
          isCountriesPage,
          regionsCountries,
          byDevice,
          titleNow,
        }) || []
      );
    }
    return [];
  }, [countryPieRaw, isHoverPie, byCountry, JSON.stringify(countryOptions), visualMetricValue]);

  const pieSeriesData = useMemo(() => {
    // 按国家时：pieData 是原始 [{metric, value, date, market, ...}] 格式
    // 需要按 market 汇总指定 metric 的 value 生成饼图扇形
    if (byCountry && Array.isArray(pieData) && pieData.length > 0) {
      // visualMetricValue 可能带数据源前缀（如 "mscience__digitalRevenue"），后端返回的 metric 不带前缀
      // 兼容两种格式：完全匹配 或 后半段匹配
      const metricSuffix = visualMetricValue?.includes('__')
        ? visualMetricValue.split('__').slice(1).join('__')
        : visualMetricValue;
      const marketTotalMap: Record<string, number> = {};
      pieData.forEach((item: any) => {
        const metric = item?.metric || item?.name;
        if (metric !== visualMetricValue && metric !== metricSuffix) return;
        const market = item?.market || '';
        if (!market) return;
        const val = Number(item?.value) || 0;
        marketTotalMap[market] = (marketTotalMap[market] || 0) + val;
      });
      if (Object.keys(marketTotalMap).length > 0) {
        const sorted = Object.entries(marketTotalMap).sort(([, a], [, b]) => b - a);
        const TOP = 10;
        const top = sorted.slice(0, TOP);
        const rest = sorted.slice(TOP);
        const othersVal = rest.reduce((sum, [, v]) => sum + v, 0);
        const result = top.map(([market, value]) => {
          const countryName = getCountryNameByCountryCode(countryOptions, market);
          return { name: countryName, name_zh: countryName, app_id: market, value };
        });
        if (othersVal > 0) {
          result.push({ name: 'Others', name_zh: 'Others', app_id: 'others', value: othersVal });
        }
        return result;
      }
    }
    return pieSeriesFormatter?.({
      list: barData,
      regionsCountries,
      legendMetric: visualMetricValue,
      calOtherList,
      allData: tableData,
    });
  }, [JSON.stringify(tableData), visualMetricValue, JSON.stringify(barData), byCountry, JSON.stringify(pieData), JSON.stringify(countryOptions)]);

  // 获取饼图 hooks
  const { pieOption } = useSinglePieCharts({
    pieData: pieSeriesData,
    pieTitle: gameName,
    setChartLoading,
    chartLoading,
    chartType,
    chartsInstance,
    titleNow,
    secondTitleRight,
    startDate: showTimeByDateType(startDate, dateType?.toLocaleLowerCase()),
    endDate: showTimeByDateType(endDate, dateType?.toLocaleLowerCase()),
    topChartsType: visualisPieTopChartsType,
    ifHasOtherHeadContent: byCountry,
  });

  const onChartEarliestClick = (data: any) => {
    setEarliestClickValue(data.value);
  };

  const lineSeriesData = useMemo(() => {
    return lineSeriesFormatter?.({
      list: lineData || [],
      legendMetric: visualMetricValue,
      dateType,
      earliestClickValue,
      titleNow,
      regionsCountries,
      isPercentLine: isPercentage,
      visualMetricValues: visualMetricValueInfo,
      visuailsMetricValues: visualMetricValuesInfo,
    });
  }, [
    dateType,
    lineData,
    visualMetricValue,
    earliestClickValue,
    visualMetricValueInfo,
    JSON.stringify(visualMetricValuesInfo),
    locale,
  ]);

  // 获取折线图 hooks
  const isTwoYAxis = Array.isArray(visualMetricValuesIds) && visualMetricValuesIds.length === 2;

  const lineSeries = (() => {
    if (!Array.isArray(visualMetricValuesInfo) || visualMetricValuesInfo.length === 0) {
      return [];
    }

    const list = lineSeriesData?.[lineMetricName] || [];
    const visualMetricValuesIds = visualMetricValuesInfo.map((item) => item?.value);
    const trueDataMetric = (() => {
      if (visualMetricValuesIds.includes('databrain__lifetime_total_revenue')) {
        if (list?.find((item: any) => item?.id === 'databrain__revenue__truedata')) {
          return 'databrain__revenue__truedata';
        }
      }

      if (visualMetricValuesIds.includes('databrain__lifetime_base_game_sales_units')) {
        if (list?.find((item: any) => item?.id === 'databrain__units__truedata')) {
          return 'databrain__units__truedata';
        }
      }
    })();
    const trueDataMetricInfo = !trueDataMetric ? null : { value: trueDataMetric, text: trueDataMetric };
    const metrics = !trueDataMetricInfo ? visualMetricValuesInfo : [...visualMetricValuesInfo, trueDataMetricInfo];

    const baseSeries = assignDistinctSeriesColors(
      metrics.map((metric) => {
        const metricData =
          list?.find((item: any) => item?.id === metric?.value) ||
          list?.find((item: any) => item?.name === metric?.text);
        // 所有数据源曲线统一附加数据源展示元数据，图例/Tooltip 均由数据源 icon 表达；
        // 数据源前缀仅在 PREFIX_DATASOURCES 内保留，用于解决同名 series 的 echarts 合并。
        const sourceDisplayMeta = getSeriesSourceDisplayMeta(metric);
        return {
          ...(sourceDisplayMeta || {}),
          type: 'line',
          id: metric?.value,
          name: metric?.text,
          step:
            isStepLine || isMultiValuePriceMetric(metric?.value) || false,
          data: metricData?.history?.map((item: any) => {
            const value = [item?.time, item?.value];
            if (!sourceDisplayMeta) return value;
            return { value, ...sourceDisplayMeta };
          }) || [],
          showSymbol: false,
          symbolSize: 10,
          lineStyle: {
            type: 'solid',
          },
          color: metricData?.color,
          cover: metricData?.cover || '',
        };
      }),
    );

    // History Hook 已按 Global 聚合、指标和日期过滤，这里只负责绑定当前父指标。
    const alineaSeries = (alineaTrueDataSeries || [])
      .map((trueDataSeries: IAlineaTrueDataSeries, index: number) => {
        const parentMetric = visualMetricValuesInfo.find(
          (item: any) => item?.value === trueDataSeries?.selection?.pageMetric,
        );
        // displaySelections 与 visualMetricValuesInfo 来源一致；找不到父指标时该真数不属于当前图表。
        if (!parentMetric) return null;
        const parentTooltipMeta = getSeriesSourceDisplayMeta(parentMetric);
        const trueDataTooltipName = formatTrueDataSeriesName(
          parentTooltipMeta.dbTooltipName,
          alineaTrueDataSuffix,
        );
        const trueDataColor = ALINEA_TRUE_DATA_COLORS[index];

        return {
          type: 'line',
          id: `${trueDataSeries.selection.pageMetric}__alinea_true_data`,
          name: formatTrueDataSeriesName(
            parentMetric.text,
            alineaTrueDataSuffix,
          ),
          dataSourceKey: parentTooltipMeta.dataSourceKey,
          dbTooltipName: trueDataTooltipName,
          data: trueDataSeries.points.map((point) => ({
            value: [point.date, point.value],
            __isTrueData: true,
            __parentName: parentMetric.text,
            dbTrueDataColor: trueDataColor,
            dataSourceKey: parentTooltipMeta.dataSourceKey,
            dbTooltipName: trueDataTooltipName,
          })),
          showSymbol: true,
          symbol: 'diamond',
          symbolSize: TRUE_DATA_CHART_DIAMOND_SIZE,
          lineStyle: { type: 'solid', color: trueDataColor },
          itemStyle: { color: trueDataColor },
          color: trueDataColor,
          dbIsTrueData: true,
          dbParentMetric: trueDataSeries.selection.pageMetric,
        };
      })
      .filter(Boolean);

    return [...baseSeries, ...alineaSeries];
  })();

  const lineXAxisData = (() => {
    // 没有新增真数时交回公共 Hook 推导 X 轴，确保普通 KPI Trends 行为完全不变。
    if (!alineaTrueDataSeries.length) return undefined;
    const dates = new Set<string>();
    lineSeries.forEach((series: any) => {
      (series?.data || []).forEach((point: any) => {
        const value = Array.isArray(point) ? point : point?.value;
        if (Array.isArray(value) && value[0]) {
          dates.add(value[0]);
        }
      });
    });

    // 类目轴必须覆盖所有附加 series 的日期，否则 History 有返回值也无法落位。
    return getSortedDates(Array.from(dates), 'asc');
  })();

  /** true 表示至少有一条 Alinea History 点位，普通趋势 noData 不能覆盖它。 */
  const hasRenderableAlineaTrueData = lineSeries.some(
    (series: any) =>
      series?.dbIsTrueData &&
      series?.data?.length > 0,
  );
  /** true 表示当前至少一条曲线需要用数据源 icon 替代名称前缀。 */
  const hasSeriesSourceDisplayMeta = lineSeries.some(
    (series: any) => series?.dataSourceKey && series?.dbTooltipName,
  );

  const { lineOption: baseLineOption } = useLineCharts({
    legendSelected,
    isStackLine,
    lineSeriesData: byDevice ? undefined : lineSeries,
    lineData: lineSeriesData,
    lineXAxisData,
    metric: lineMetricName,
    topChartsType: visualisLineTopChartsType,
    isTwoYAxis,
    visuailsMetricValues: visualMetricAxisInfo,
    imageType,
    titleNow,
    chartType,
    setChartLoading,
    dateType,
    isPercentLine: isPercentage,
    isNaNeedMax,
    isStep: isStepLine,
    ifHasOtherHeadContent: byDevice || byCountry,
    baseReleaseTime: showTimeByDateType(
      baseReleaseTime,
      dateType?.toLocaleLowerCase(),
    ),
    earliestPeriod: showTimeByDateType(
      earliestPeriod,
      dateType?.toLocaleLowerCase(),
    ),
    startDate: showTimeByDateType(startDate, dateType?.toLocaleLowerCase()),
    endDate: showTimeByDateType(endDate, dateType?.toLocaleLowerCase()),
    secondTitleRight,
    lineSourceName,
    lineSourceIcon,
    gridConfig,
    useExternalAxisTitles: showExternalAxisTitles,
    // hover图例时其他线条的透明度设置为20%
    legendHoverOpacity: 0.2,
    // series的特殊配置
    specialSeriesOption: {
      // hover时数据点的opacity
      emphasisOpacity: 0,
    },
    // 仅需 icon 消歧的 series 透传展示元数据，其他普通趋势维持公共 Hook 原行为。
    shouldPreserveSeriesDisplayMeta: hasSeriesSourceDisplayMeta,
  });

  const lineOption = useMemo(() => {
    const optionWithSeriesLegend = applySeriesLegendDisplay(
      baseLineOption,
      lineSourceIcon,
    );
    if (!isKpiTrendsHourlyGranularity(dateType)) {
      return optionWithSeriesLegend;
    }
    return applyKpiTrendsHourlyLineXAxis(
      optionWithSeriesLegend,
      lineSeriesData,
      lineMetricName,
    );
  }, [
    baseLineOption,
    dateType,
    lineMetricName,
    lineSeriesData,
    lineSourceIcon,
  ]);

  const axisTitlesClassName = showExternalAxisTitles
    ? 'visualis-axis-titles visualis-axis-titles--dual'
    : 'visualis-axis-titles';

  /**
   * 高度变化时，重绘echarts
   */
  useEffect(() => {
    chartsInstance.current?.getLineInstance()?.resize();
  }, [chartHeight]);

  return (
    <div className="card-line" ref={cardRef}>
      {showExternalAxisTitles && (
        <div className={axisTitlesClassName}>
          <span className="visualis-axis-titles__item visualis-axis-titles__item--left">
            {axisHeaderTexts[0]}
          </span>
          {axisHeaderTexts.length > 1 && (
            <span className="visualis-axis-titles__item visualis-axis-titles__item--right">
              {axisHeaderTexts[1]}
            </span>
          )}
        </div>
      )}
      <OpinionBarAndLineChart
        chartHeight={chartHeight}
        nodataMsg={chartNodataMsg}
        ref={chartsInstance}
        chartLoading={ifUseMarketLineLoading ? marketLineLoading : chartLoading}
        lineOption={lineOption}
        replaceSeriesOnUpdate
        pieOption={isHoverPie ? null : pieOption}
        pieData={pieListData}
        chartType={chartType}
        isNodata={
          isNodata ||
          (chartType === ChartType.Line &&
            lineSeriesData?.noData &&
            !hasRenderableAlineaTrueData)
        }
        onChartEarliestClick={onChartEarliestClick}
        isHoverPie={isHoverPie}
        metricText={titleNow}
        startDate={showTimeByDateType(startDate, dateType?.toLocaleLowerCase())}
        endDate={showTimeByDateType(endDate, dateType?.toLocaleLowerCase())}
        topChartsType={visualisPieTopChartsType}
        byDevice={byDevice}
        secondTitleRight={byDevice ? null : secondTitleRight}
        ifLineChartLegendOn={ifLineChartLegendOn}
        ifHasOtherHeadContent={byDevice || byCountry}
        enableEventAnnotation={!!enableOpinionEventAnnotation}
        opinionEventConfig={opinionEventConfig}
        controlledEventAnnotation={controlledEventAnnotation}
      />
    </div>
  );
};

export default Visualis;
